import { TabsV2 } from '@medeloop/medeloop-components-library';
import { useState, useEffect } from 'react';
import CodesTabContent from './codes-tab-content.tsx';
import { formatRelevance } from './utils.ts';
import { useGetCodesetById } from 'src/api/codesets-queries.ts';

interface CodesTableProps {
  data: {
    table_name: string;
    search_term: string;
    matches: {
      saved: {
        code_list_id: string;
      };
      recommended: {
        name: string;
        description: string;
        data: Array<{
          concept_id: string;
          concept_name: string;
          concept_count: number;
          relevance: number;
          enabled: boolean;
        }>;
      };
    };
  };
  tableIndex: number;
  jobId: string;
  className?: string;
}

export const CodesTable: React.FC<CodesTableProps> = ({
  data,
  tableIndex,
  jobId,
  className = '',
}) => {
  const { data: savedCodesetPayload, isLoading } = useGetCodesetById(
    data.matches.saved.code_list_id,
    {
      enabled: data?.matches?.saved?.code_list_id !== '',
    },
  );
  const [codeListId, setCodeListId] = useState<string>(data.matches.saved.code_list_id);
  const hasSavedCodeset = codeListId !== '';
  const [activeTab, setActiveTab] = useState<string>(hasSavedCodeset ? 'saved' : 'recommended');

  // Fetch codeset data when ID changes
  useEffect(() => {
    if (codeListId) {
      setActiveTab('saved');
    } else {
      setActiveTab('recommended');
    }
  }, [codeListId]);

  // Initialize active tab on first render
  useEffect(() => {
    setActiveTab(hasSavedCodeset ? 'saved' : 'recommended');
  }, []);

  // Handle when a codeset is saved (created or updated)
  const handleCodesetSaved = (savedCodesetId: string) => {
    setCodeListId(savedCodesetId);
  };

  return (
    <div className={`bg-purple-gray-900 rounded-lg ${className}`}>
      <TabsV2 defaultValue={activeTab}>
        <TabsV2.List>
          {hasSavedCodeset && <TabsV2.Trigger value="saved">Saved Codes</TabsV2.Trigger>}
          <TabsV2.Trigger value="recommended">Suggested by Medeloop</TabsV2.Trigger>
        </TabsV2.List>

        {!isLoading && hasSavedCodeset && savedCodesetPayload && (
          <TabsV2.Content
            value="saved"
            className="p-4 border border-slate-800 rounded-b-2xl rounded-tr-2xl bg-slate-950"
          >
            <CodesTabContent
              table_name={savedCodesetPayload.tableName}
              codeset_name={savedCodesetPayload.codesetName}
              codeset_description={savedCodesetPayload.codesetDescription}
              search_term={savedCodesetPayload.searchTerm}
              items={savedCodesetPayload.data.map((x) => ({
                ...x,
                relevance: formatRelevance(x.relevance),
                id: x.concept_id,
              }))}
              isFromSavedCodeset={true}
              initialSavedCodeId={codeListId}
              tableIndex={tableIndex}
              jobId={jobId}
              onCodesetSaved={handleCodesetSaved}
            />
          </TabsV2.Content>
        )}

        <TabsV2.Content
          value="recommended"
          className="p-4 border border-[#1E2134] rounded-b-2xl rounded-tr-2xl bg-[#0A0D16]"
        >
          <CodesTabContent
            table_name={data.table_name}
            codeset_name={data.matches.recommended.name}
            codeset_description={data.matches.recommended.description}
            search_term={data.search_term}
            items={data.matches.recommended.data.map((x) => ({
              ...x,
              relevance: formatRelevance(x.relevance),
              id: x.concept_id,
            }))}
            initialSavedCodeId={codeListId}
            tableIndex={tableIndex}
            jobId={jobId}
            onCodesetSaved={handleCodesetSaved}
          />
        </TabsV2.Content>
      </TabsV2>
    </div>
  );
};
